<template>
  <Teleport to="body">
    <Transition name="welcome-popup" appear>
      <div
        v-if="visible"
        class="welcome-popup-overlay"
        role="presentation"
        @click.self="close"
      >
        <div
          ref="cardRef"
          class="welcome-popup"
          :class="`welcome-popup--${popup.image_position}`"
          role="dialog"
          aria-modal="true"
          :aria-label="localized.title"
          tabindex="-1"
          @keydown.esc="close"
        >
          <button
            v-if="popup.show_close"
            type="button"
            class="welcome-popup__close"
            aria-label="Cerrar"
            @click="close"
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M6 6L18 18M18 6L6 18" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
            </svg>
          </button>

          <div v-if="imageSrc" class="welcome-popup__media">
            <img :src="imageSrc" :alt="localized.title" class="welcome-popup__img" />
          </div>

          <div class="welcome-popup__content">
            <img src="@/assets/images/logo.png" alt="Logo" class="welcome-popup__logo" />
            <p class="welcome-popup__subtitle">{{ localized.subtitle }}</p>

            <div class="welcome-popup__actions">
              <button
                type="button"
                class="welcome-popup__btn welcome-popup__btn--primary"
                @click="goAction(popup.primary_button_action)"
              >
                {{ localized.primary }}
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </button>
              <button
                type="button"
                class="welcome-popup__btn welcome-popup__btn--secondary"
                @click="goAction(popup.secondary_button_action)"
              >
                {{ localized.secondary }}
              </button>
            </div>

            <p v-if="popup.show_close" class="welcome-popup__dismiss">
              <button type="button" class="welcome-popup__dismiss-link" @click="close">
                {{ t('welcomePopup.notNow') }}
              </button>
            </p>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from '@/composables/useI18n'
import { siteContactPublicRef } from '@/composables/useSiteContact'
import {
  normalizeWelcomePopup,
  resolveWelcomeImage,
  WELCOME_POPUP_STORAGE_KEY,
} from '@/modules/welcome/config'

const router = useRouter()
const route = useRoute()
const { locale, t } = useI18n()

const visible = ref(false)
const cardRef = ref(null)
const shownAt = ref(0)

// ── Config del pop-up (reactiva al siteConfig público) ───────────────────
const rawPopup = computed(() => {
  const cfg = siteContactPublicRef.value
  return (cfg && typeof cfg === 'object' && cfg.welcome_popup) || null
})
const popup = computed(() => normalizeWelcomePopup(rawPopup.value))

// Textos resueltos por idioma activo (EN usa translations.en con fallback ES).
const localized = computed(() => {
  const p = popup.value
  const en = p.translations && p.translations.en ? p.translations.en : {}
  if (locale.value !== 'en') {
    return {
      title: p.title,
      subtitle: p.subtitle,
      primary: p.primary_button_text,
      secondary: p.secondary_button_text,
    }
  }
  return {
    title: en.title || p.title,
    subtitle: en.subtitle || p.subtitle,
    primary: en.primary_button_text || p.primary_button_text,
    secondary: en.secondary_button_text || p.secondary_button_text,
  }
})

const imageSrc = computed(() => resolveWelcomeImage(popup.value.image))

// ── Frecuencia: cuándo volver a mostrar ───────────────────────────────────
function shouldShow(frequency) {
  const stored = localStorage.getItem(WELCOME_POPUP_STORAGE_KEY)
  if (frequency === 'once') return !stored
  if (frequency === 'daily') {
    const today = new Date().toISOString().slice(0, 10)
    return stored !== today
  }
  return true // always
}

function rememberShown(frequency) {
  if (frequency === 'once') {
    localStorage.setItem(WELCOME_POPUP_STORAGE_KEY, '1')
  } else if (frequency === 'daily') {
    localStorage.setItem(WELCOME_POPUP_STORAGE_KEY, new Date().toISOString().slice(0, 10))
  }
}

// ── Apertura / cierre ─────────────────────────────────────────────────────
function isAuthenticated() {
  return Boolean(localStorage.getItem('accessToken'))
}

function open() {
  visible.value = true
  shownAt.value = Date.now()
  document.body.classList.add('welcome-popup-lock')
  nextTick(() => {
    cardRef.value?.focus()
  })
}

function close() {
  visible.value = false
  document.body.classList.remove('welcome-popup-lock')
}

function tryShow() {
  if (visible.value || !popup.value.enabled || isAuthenticated()) return
  if (!shouldShow(popup.value.frequency)) return
  const delay = (popup.value.delay_seconds || 0) * 1000
  setTimeout(open, delay)
}

function goAction(action) {
  rememberShown(popup.value.frequency)
  close()
  const target = action === 'register' ? '/crear-cuenta' : '/iniciar-sesion'
  if (route.path !== target) {
    router.push(target)
  }
}

// ── Ciclo de vida ─────────────────────────────────────────────────────────
let focusTrapHandler = null
let observer = null

onMounted(() => {
  // El siteConfig público puede tardar en cargar; reintentamos al cambiar.
  if (rawPopup.value) {
    tryShow()
  } else {
    observer = watch(siteContactPublicRef, () => {
      if (rawPopup.value) {
        observer()
        tryShow()
      }
    })
  }

  // Focus trap simple: mantiene el foco dentro del modal mientras está abierto.
  focusTrapHandler = (event) => {
    if (!visible.value) return
    if (event.key !== 'Tab') return
    const focusables = cardRef.value?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    if (!focusables || !focusables.length) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }
  document.addEventListener('keydown', focusTrapHandler)
})

onBeforeUnmount(() => {
  if (focusTrapHandler) document.removeEventListener('keydown', focusTrapHandler)
  if (observer) observer()
  document.body.classList.remove('welcome-popup-lock')
})

// Si el usuario cierra y luego navega (sin recargar), no reabrir en la misma sesión corta.
watch(route, () => {
  if (visible.value && Date.now() - shownAt.value < 800) return
})
</script>

<style scoped>
.welcome-popup-overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(10, 10, 10, 0.62);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

.welcome-popup__logo {
  width: 280px;
  height: auto;
  margin-bottom: 1.2rem;
}
.welcome-popup {
  position: relative;
  display: grid;
  width: min(880px, 100%);
  max-height: min(640px, 92vh);
  overflow: hidden;
  background: #ffffff;
  border-radius: 0;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.35);
  outline: none;
}

.welcome-popup--left { grid-template-columns: 40% 1fr; }
.welcome-popup--right { grid-template-columns: 1fr 50%; }
.welcome-popup--top { grid-template-columns: 1fr; }

.welcome-popup--left .welcome-popup__media { order: 0; }
.welcome-popup--right .welcome-popup__media { order: 1; }

.welcome-popup__media {
  min-height: 265px;
  background: #f2f2f2;
}

.welcome-popup__img {
  width: 100%;
  height: 100%;
  min-height: 285px;
  max-height: 500px;
  object-fit: cover;
  display: block;
}

.welcome-popup--top .welcome-popup__img {
  max-height: 260px;
}

.welcome-popup__content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2.6rem 2.4rem;
  min-height: 0;
  overflow: hidden;
}


.welcome-popup__subtitle {
  margin: 0 0 1.8rem;
  font-size: .9rem;
  line-height: 1.6;
  max-width: 340px;
  color: rgba(17, 17, 17, 0.72);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
  overflow: hidden;
}

.welcome-popup__actions {
  display: flex;
  gap: 0.8rem;
  width: 100%;
  flex-wrap: wrap;
}

.welcome-popup__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  min-height: 42px;
  padding: 0 1rem;
  border: 1px solid transparent;
  font-size: 0.8rem;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  width:100%;
  transition: background-color 180ms ease, color 180ms ease, border-color 180ms ease;
}

.welcome-popup__btn svg {
  width: 15px;
  height: 15px;
}

.welcome-popup__btn--primary {
  background: #111111;
  color: #ffffff;
}

.welcome-popup__btn--primary:hover {
  background: #2c2c2c;
}

.welcome-popup__btn--secondary {
  background: transparent;
  border-color: #111111;
  color: #111111;
}

.welcome-popup__btn--secondary:hover {
  background: rgba(17, 17, 17, 0.06);
}

.welcome-popup__dismiss {
  margin: 1.1rem 0 0;
}

.welcome-popup__dismiss-link {
  background: none;
  border: none;
  padding: 0.25rem 0.5rem;
  font-size: 0.78rem;
  color: rgba(17, 17, 17, 0.55);
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}

.welcome-popup__dismiss-link:hover {
  color: #111111;
}

.welcome-popup__close {
  position: absolute;
  top: 0.7rem;
  right: 0.7rem;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  color: #111111;
  cursor: pointer;
  transition: background-color 180ms ease, transform 180ms ease;
}

.welcome-popup__close:hover {
  background: #ffffff;
  transform: rotate(90deg);
}

.welcome-popup__close svg {
  width: 16px;
  height: 16px;
}

/* Transición */
.welcome-popup-enter-active,
.welcome-popup-leave-active {
  transition: opacity 260ms ease;
}

.welcome-popup-enter-active .welcome-popup,
.welcome-popup-leave-active .welcome-popup {
  transition: transform 260ms ease, opacity 260ms ease;
}

.welcome-popup-enter-from,
.welcome-popup-leave-to {
  opacity: 0;
}

.welcome-popup-enter-from .welcome-popup,
.welcome-popup-leave-to .welcome-popup {
  opacity: 0;
  transform: translateY(18px) scale(0.985);
}

/* Scroll bloqueado mientras está abierto */
:global(body.welcome-popup-lock) {
  overflow: hidden;
}

@media (max-width: 720px) {
  .welcome-popup,
  .welcome-popup--left,
  .welcome-popup--right,
  .welcome-popup--top {
    grid-template-columns: 1fr;
  }

  .welcome-popup__media,
  .welcome-popup--left .welcome-popup__media,
  .welcome-popup--right .welcome-popup__media {
    order: 0;
    min-height: 0;
    aspect-ratio: 4 / 3;
  }

  .welcome-popup__img {
    width: 100%;
    height: 100%;
    min-height: 0;
    max-height: none;
    object-fit: cover;
  }

  .welcome-popup__content {
    padding: 1.8rem 1.4rem 1.6rem;
    overflow: visible;
  }
}
</style>
